import type { Volume } from '../types/content'

export const volumes: Volume[] = [
  {
    number: 1,
    title: 'MTB Foundations, Rider Assessment & Coaching Methodology',
    tagline: 'Build the rider correctly before adding more speed, terrain or complexity.',
    intro: [
      'Every strong MTB progression starts with understanding the rider in front of you.',
      'If balance, vision, body position, braking or basic control are weak, adding more speed or harder terrain doesn’t solve the problem.',
      'It exposes it.',
      'This volume gives you a professional framework for assessing riders and building the foundation that every later skill depends on.',
    ],
    lists: [
      {
        label: 'Inside you’ll work with:',
        items: [
          'Rider baseline assessment',
          'Neutral and ready positions',
          'Balance and body awareness',
          'Vision fundamentals',
          'Steering control',
          'Basic braking',
          'Pedal positioning',
          'Climbing foundations',
          'Descending foundations',
          'Coaching methodology',
          'Rider observation',
          'Regression decisions',
          'Progression criteria',
          'Foundation trail application',
        ],
      },
    ],
    result:
      'You know where the rider is, what is limiting them and exactly where their progression should begin.',
    tone: 'white',
  },
  {
    number: 2,
    title: 'Bike Handling, Balance & Technical Control',
    tagline: 'Give riders the precision they need before technical terrain becomes more demanding.',
    intro: [
      'Many riders look comfortable while the trail is open and predictable.',
      'Then the line becomes narrow. The speed drops. An obstacle appears. A tight technical section arrives.',
      'And suddenly every weakness becomes visible.',
      'This volume develops the precision and bike control required for technical riding.',
    ],
    lists: [
      {
        label: 'Inside you’ll work with:',
        items: [
          'Slow-speed balance',
          'Bike-body separation',
          'Precise steering',
          'Pedal positioning',
          'Ratcheting',
          'Front-wheel placement',
          'Front-wheel lightening',
          'Rear-wheel unweighting',
          'Rear-wheel tracking',
          'Narrow technical lines',
          'Low obstacle control',
          'Wheel control',
          'Technical maneuvering',
          'Progressive technical circuits',
        ],
      },
    ],
    result:
      'Your riders become more precise, more adaptable and more confident when the trail demands real control.',
    tone: 'mint',
  },
  {
    number: 3,
    title: 'Cornering, Braking & Line Choice Systems',
    tagline:
      'Teach riders to enter with control, choose better lines and finish every corner ready for what comes next.',
    intro: [
      'Most cornering mistakes don’t begin in the middle of the turn.',
      'They begin before the rider ever starts turning.',
      'Too much speed. Late braking. Poor vision. Bad line selection. Rigid body position.',
      'Then the rider enters the corner with too many problems to solve at once.',
      'This volume gives you a complete system for diagnosing and correcting those mistakes.',
    ],
    lists: [
      {
        label: 'Inside you’ll teach:',
        items: [
          'Entry-speed control',
          'Progressive braking',
          'Brake modulation',
          'Brake-release timing',
          'Vision through the turn',
          'Bike lean',
          'Bike-body separation',
          'Flat corners',
          'Berms',
          'Wide, middle and tight lines',
          'Turning timing',
          'Traction management',
          'Linked corners',
          'Switchbacks',
          'Loose surfaces',
          'Low-grip conditions',
          'Line-choice decisions',
        ],
      },
    ],
    result: 'Your riders stop surviving corners and start understanding them.',
    tone: 'sand',
  },
  {
    number: 4,
    title: 'Climbing, Descending & Technical Terrain Development',
    tagline: 'Build control where riders usually lose it.',
    intro: [
      'Technical terrain exposes weak technique quickly.',
      'Poor pedal timing. Bad traction. Late gear changes. Reactive braking. Rigid body position. Weak line choice.',
      'This volume gives you a structured progression for developing the skills riders need on climbs, descents, roots, rocks and technical terrain.',
    ],
    lists: [
      {
        label: 'Inside you’ll cover:',
        items: [
          'Climbing position',
          'Technical climbing',
          'Gear preparation',
          'Smooth power',
          'Traction management',
          'Pedal timing',
          'Climbing line choice',
          'Descending position',
          'Brake modulation',
          'Roots',
          'Rocks',
          'Rollers',
          'Terrain absorption',
          'Technical transitions',
          'Roll-downs',
          'Chutes',
          'Linked trail features',
          'Natural-terrain application',
        ],
      },
    ],
    result:
      'Your riders approach technical terrain with a plan instead of reacting at the last second.',
    tone: 'white',
  },
  {
    number: 5,
    title: 'Speed, Flow, Trail Reading & Advanced Riding Decisions',
    tagline: 'Move beyond isolated skills and teach riders how to connect the trail.',
    intro: [
      'A rider can know how to brake. Know how to corner. Know how to ride rollers.',
      'And still struggle once all those demands arrive together.',
      'That’s where flow, anticipation and decision-making become essential.',
      'This volume teaches riders to stop processing every trail feature separately and start reading the trail as a complete sequence.',
    ],
    lists: [
      {
        label: 'Inside you’ll develop:',
        items: [
          'Near / Mid / Far vision',
          'Trail scanning',
          'Momentum management',
          'Control zones',
          'Flow zones',
          'Brake-release timing',
          'Pumping foundations',
          'Pedal vs coast decisions',
          'Roller rhythm',
          'Linked corners',
          'Feature linking',
          'Braking economy',
          'Rider resets',
          'Line choice for momentum',
          'Decision windows',
          'Changing conditions',
          'Rider-led decisions',
          'Independent trail riding',
        ],
      },
    ],
    result: 'Your riders stop reacting to the trail and start reading it.',
    tone: 'mint',
  },
  {
    number: 6,
    title: '150 Ready-to-Coach MTB Sessions & Rider Development System',
    tagline: 'Eliminate the question: “What should I teach today?”',
    intro: [
      'Knowing MTB technique is one thing.',
      'Turning that knowledge into complete coaching sessions week after week is another.',
      'This volume gives you a library of:',
    ],
    highlight: {
      title: '150 Ready-to-Coach MTB Sessions',
      note: 'covering the complete rider-development pathway.',
    },
    lists: [
      {
        label: 'Sessions include areas such as:',
        items: [
          'Foundation skills',
          'Balance',
          'Bike handling',
          'Braking',
          'Cornering',
          'Line choice',
          'Wheel control',
          'Climbing',
          'Descending',
          'Technical terrain',
          'Roots and rocks',
          'Flow',
          'Trail reading',
          'Decision-making',
          'Integrated rider development',
        ],
      },
      {
        label: 'And practical coaching elements such as:',
        items: [
          'Session objective',
          'Rider level',
          'Recommended terrain',
          'Equipment',
          'Setup',
          'Session structure',
          'Coaching cues',
          'Common mistakes',
          'Corrections',
          'Regressions',
          'Progressions',
          'Success criteria',
          'Recommended next step',
        ],
      },
    ],
    result: 'Open the system. Identify the rider’s limiter. Choose the session. Coach.',
    closing: ['No blank page.', 'No last-minute searching.', 'No wondering what comes next.'],
    tone: 'sand',
  },
]
